import { faArrowLeft, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
    getUpgradePlans,
    requestUpgrade,
} from '../../services/UpgradeRequestsService';
import PlansModal from './PlansModal';

function UpgradeRequest() {
    const [searchParams] = useSearchParams();
    const { state } = useLocation();
    const currentPlanId = state?.currentPlanId;

    const [email, setEmail] = useState(searchParams.get('email') || '');
    const [planId, setPlanId] = useState('');
    const [note, setNote] = useState('');
    const [plans, setPlans] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        getUpgradePlans()
            .then(result => {
                setPlans(result);
                const available = result.find(
                    plan => plan.id !== currentPlanId,
                );
                if (available) setPlanId(String(available.id));
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setError('Could not load the plans. Try again later.');
            });
    }, [currentPlanId]);

    function onSubmit(event) {
        event.preventDefault();

        setError('');
        setSending(true);
        requestUpgrade(email, Number(planId), note)
            .then(result => setSuccess(result.message))
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setError(
                    err.response?.status === 429
                        ? 'Too many attempts. Try again later.'
                        : 'Could not send your request. Check the fields and try again.',
                );
            })
            .finally(() => setSending(false));
    }

    function formatPlan(plan) {
        const price = plan.priceMonthly
            ? ` - $${Number(plan.priceMonthly).toFixed(2)}/mo.`
            : '';
        const current = plan.id === currentPlanId ? ' (current)' : '';
        return `${plan.name}${price}${current}`;
    }

    const hasOnlyCurrentPlan =
        plans.length > 0 && plans.every(plan => plan.id === currentPlanId);

    return (
        <main>
            <section className="vh-lg-100 mt-5 mt-lg-0 bg-soft d-flex align-items-center">
                <div className="container">
                    <p className="text-center">
                        <Link
                            to="/"
                            className="d-flex align-items-center justify-content-center"
                        >
                            <FontAwesomeIcon
                                icon={faArrowLeft}
                                className="icon icon-xs me-2 my-vb-icon"
                            />
                            Back to sign in
                        </Link>
                    </p>
                    <div className="row justify-content-center form-bg-image">
                        <div className="col-12 d-flex align-items-center justify-content-center">
                            <div className="bg-white shadow border-0 rounded border-light p-4 p-lg-5 w-100 fmxw-500">
                                <div className="text-center text-md-center mb-4 mt-md-0">
                                    <h1 className="mb-0 h3">
                                        Keep your account
                                    </h1>
                                    <p className="text-muted small mt-2">
                                        Choose a plan and send your request. We
                                        will review it and email you a secure
                                        payment link.
                                    </p>
                                </div>
                                {success ? (
                                    <div className="alert alert-success">
                                        {success}
                                    </div>
                                ) : (
                                    <form action="#" onSubmit={onSubmit}>
                                        <div className="form-group mb-4">
                                            <label htmlFor="email">
                                                Your Email
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <FontAwesomeIcon
                                                        icon={faEnvelope}
                                                        className="icon icon-xs text-gray-600 my-vb-icon"
                                                    />
                                                </span>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    placeholder="example@company.com"
                                                    id="email"
                                                    value={email}
                                                    autoFocus
                                                    required
                                                    onChange={event =>
                                                        setEmail(
                                                            event.target.value,
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group mb-4">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <label htmlFor="planId">
                                                    Plan
                                                </label>
                                                <button
                                                    type="button"
                                                    className="btn btn-link btn-sm p-0"
                                                    data-bs-toggle="modal"
                                                    data-bs-target="#modalPlans"
                                                    disabled={!plans.length}
                                                >
                                                    Compare plans
                                                </button>
                                            </div>
                                            <select
                                                id="planId"
                                                className="form-select"
                                                value={planId}
                                                required
                                                onChange={event =>
                                                    setPlanId(
                                                        event.target.value,
                                                    )
                                                }
                                            >
                                                {plans.map(plan => (
                                                    <option
                                                        key={plan.id}
                                                        value={plan.id}
                                                        disabled={
                                                            plan.id ===
                                                            currentPlanId
                                                        }
                                                    >
                                                        {formatPlan(plan)}
                                                    </option>
                                                ))}
                                            </select>
                                            {hasOnlyCurrentPlan ? (
                                                <div className="form-text">
                                                    You are already on the
                                                    highest available plan.
                                                </div>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                        <div className="form-group mb-4">
                                            <label htmlFor="note">
                                                Note (optional)
                                            </label>
                                            <textarea
                                                id="note"
                                                className="form-control"
                                                rows="3"
                                                maxLength="1000"
                                                value={note}
                                                onChange={event =>
                                                    setNote(event.target.value)
                                                }
                                            />
                                        </div>
                                        <div className="d-grid">
                                            <button
                                                type="submit"
                                                className="btn btn-gray-800"
                                                disabled={sending || !planId}
                                            >
                                                Send request
                                            </button>
                                        </div>
                                        {error ? (
                                            <div className="alert alert-danger mt-2">
                                                {error}
                                            </div>
                                        ) : (
                                            <></>
                                        )}
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <PlansModal plans={plans} currentPlanId={currentPlanId} />
        </main>
    );
}

export default UpgradeRequest;
